import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, BookOpen, Quote, Sparkles } from 'lucide-react';

const UniversityOfChicago: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "The University of Chicago";
    const shortName = schoolName.includes("Chicago") ? "UChicago" : schoolName.split(' ')[0];

    return (
        <div className="min-h-screen bg-white font-serif text-[#800000] selection:bg-[#800000] selection:text-white">
            {/* Top Bar */}
            <div className="bg-[#f2f2f2] text-[#333] py-2 px-8 flex justify-end gap-6 text-[11px] font-sans font-bold uppercase tracking-widest border-b border-gray-300">
                <a href="#" className="hover:text-[#800000]">Directory</a>
                <a href="#" className="hover:text-[#800000]">Offices & Services</a>
                <a href="#" className="hover:text-[#800000]">Visitors</a>
                <a href="#" className="hover:text-[#800000]">Give</a>
            </div>

            {/* Navbar - Traditional & Clean */}
            <nav className="bg-white py-8 px-8 border-b border-gray-100">
                <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex flex-col items-center md:items-start">
                        <span className="font-serif text-3xl md:text-4xl font-bold tracking-tight leading-none text-[#800000] uppercase">{schoolName}</span>
                    </div>

                    <div className="flex flex-wrap justify-center gap-8 font-sans font-bold text-sm text-[#333] tracking-wide">
                        <a href="#" className="hover:text-[#800000] border-b-2 border-transparent hover:border-[#800000] pb-1 transition-all">About</a>
                        <a href="#" className="hover:text-[#800000] border-b-2 border-transparent hover:border-[#800000] pb-1 transition-all">Admissions</a>
                        <a href="#" className="hover:text-[#800000] border-b-2 border-transparent hover:border-[#800000] pb-1 transition-all">Academics</a>
                        <a href="#" className="hover:text-[#800000] border-b-2 border-transparent hover:border-[#800000] pb-1 transition-all">Research</a>
                        <a href="#" className="hover:text-[#800000] border-b-2 border-transparent hover:border-[#800000] pb-1 transition-all">Campus Life</a>
                        <Search size={20} className="text-[#800000] cursor-pointer" />
                    </div>
                </div>
            </nav>

            {/* Hero - Intellectual Vibe */}
            <header className="relative bg-[#333] text-white overflow-hidden min-h-[600px] flex items-center">
                 <img 
                    src="https://images.unsplash.com/photo-1548504778-439589a16f21?q=80&w=2071&auto=format&fit=crop" 
                    alt="University Library" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-multiply"
                />
                <div className="max-w-[1200px] mx-auto px-8 relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="w-16 h-1 bg-[#800000] mb-8"></div>
                        <h1 className="text-5xl md:text-7xl font-serif leading-tight mb-8">
                            Life of the Mind.
                        </h1>
                        <p className="text-xl font-light text-gray-200 leading-relaxed mb-10 max-w-lg font-sans">
                            At {shortName}, we are driven by an intense curiosity and a fearless commitment to inquiry.
                        </p>
                        <Button className="bg-[#800000] hover:bg-[#600000] text-white font-sans font-bold uppercase tracking-widest px-8 h-14 rounded-none text-xs">
                            Explore the College
                        </Button>
                    </div>
                </div>
            </header>
            
            {/* Quote / Academic Focus */}
            <section className="bg-[#f9f9f9] py-24 px-8 border-b-8 border-[#800000]">
                <div className="max-w-[1000px] mx-auto text-center">
                    <Quote className="text-[#800000]/20 mx-auto w-16 h-16 mb-6" />
                    <blockquote className="text-3xl md:text-4xl font-serif leading-tight text-[#333] mb-8">
                        "Education is not the filling of a pail, but the lighting of a fire."
                    </blockquote>
                    <p className="text-sm font-sans font-bold uppercase tracking-widest text-[#800000]">— Common Reading, Class of 2029</p>
                </div>
            </section>

            {/* Impact Grid */}
            <section className="py-24 px-8 max-w-[1200px] mx-auto">
                <div className="flex justify-between items-baseline mb-16">
                    <h2 className="text-4xl font-serif text-[#333]">{shortName} Impact</h2>
                    <a href="#" className="font-sans font-bold text-[#800000] uppercase tracking-widest text-xs border-b border-[#800000] pb-1">All Stories</a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-16">
                    {[
                        { title: "Economic Policy", desc: "Nobel laureate leads new initiative on global markets.", img: "https://images.unsplash.com/photo-1526304640152-d4619684e484?q=80&w=2070&auto=format&fit=crop" },
                        { title: "Urban Science", desc: "Data-driven approaches to solving complex city problems.", img: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?q=80&w=2070&auto=format&fit=crop" },
                        { title: "Astrophysics", desc: "Mapping the earliest light of the universe with new precision.", img: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?q=80&w=2022&auto=format&fit=crop" }
                    ].map((item, i) => (
                        <div key={i} className="group cursor-pointer">
                            <div className="h-64 mb-6 overflow-hidden relative">
                                <img src={item.img} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt={item.title} />
                                <div className="absolute inset-0 border-4 border-transparent group-hover:border-[#800000] transition-colors duration-300"></div>
                            </div>
                            <h3 className="text-2xl font-serif font-bold text-[#333] mb-3 group-hover:text-[#800000] transition-colors">{item.title}</h3>
                            <p className="text-gray-600 font-sans leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#222] text-white py-20 px-8">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div className="col-span-1 md:col-span-2">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/cd/University_of_Chicago_Coat_of_Arms.svg/1200px-University_of_Chicago_Coat_of_Arms.svg.png" className="h-24 w-auto mb-8 bg-white p-2 rounded-sm opacity-90 grayscale hover:grayscale-0 transition-all" alt="Coat of Arms"/>
                        <p className="font-serif text-2xl text-gray-300 mb-2">{schoolName}</p>
                        <p className="font-sans text-sm text-gray-500 max-w-xs">
                            5801 S. Ellis Avenue <br/>
                            Chicago, Illinois 60637 <br/>
                            773.702.1234
                        </p>
                     </div>

                     <div>
                         <h4 className="font-sans font-bold text-gray-400 uppercase tracking-widest text-xs mb-6">Resources</h4>
                         <ul className="space-y-3 font-sans text-sm text-gray-300">
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Alumni</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Faculty & Staff</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Parents</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Students</a></li>
                         </ul>
                     </div>

                     <div>
                         <h4 className="font-sans font-bold text-gray-400 uppercase tracking-widest text-xs mb-6">Quick Links</h4>
                         <ul className="space-y-3 font-sans text-sm text-gray-300">
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Academic Calendar</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Library</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Maps</a></li>
                             <li><a href="#" className="hover:text-white border-b border-transparent hover:border-white transition-all">Job Opportunities</a></li>
                         </ul>
                     </div>
                </div>
                <div className="max-w-[1200px] mx-auto mt-16 pt-8 border-t border-gray-800 flex justify-between text-[10px] font-sans text-gray-500 uppercase tracking-widest">
                    <span>© 2026 {schoolName}</span>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-white">Emergency Info</a>
                        <a href="#" className="hover:text-white">Privacy</a>
                        <a href="#" className="hover:text-white">Accessibility</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default UniversityOfChicago;
